// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import useCreateOperatorConfiguration from '../../../hooks/mutations/registry/configuration/useCreateOperatorConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';
import { SUC_CRE_OP_CONF, ERR_CRE_OP_CONF } from '../../../utils/strings';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateOperatorConfigurationDialog = ({ open, onClose }: DialogProps) => {
  const [providerCredentialRequirements, setProviderCredentialRequirements] = useState<
    PartyCredentialRequirementElement[]
  >([]);

  const mutate = useMutate();
  const createOperatorConfiguration = useCreateOperatorConfiguration();

  const resetState = useCallback(() => {
    setProviderCredentialRequirements([]);
  }, [setProviderCredentialRequirements]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    const payload = {
      providerRequirements: toPartyCredentialRequirements(providerCredentialRequirements),
    };
    await mutate(createOperatorConfiguration, payload, SUC_CRE_OP_CONF, ERR_CRE_OP_CONF);
    handleClose();
  }, [createOperatorConfiguration, providerCredentialRequirements, handleClose, mutate]);

  return (
    <FormDialog
      open={open}
      title="Create Operator Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Create"
      isSubmitDisabled={false}
    >
      <Box mb={3}>
        <RequiredCredentialsInput
          list={providerCredentialRequirements}
          setList={setProviderCredentialRequirements}
          title="Required Credentials for Providers"
        />
      </Box>
    </FormDialog>
  );
};
